import React, { useEffect, useState, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import HighchartsReact from 'highcharts-react-official'
import { useDeviceData } from './redux/deviceData'
import Loader from '../../components/Loader'
import { getTheme } from '../../common/ambient'
import './WindRoseWidget.less'

// Process wind data into wind rose format
function processWindDataForRose(windData) {
  // Direction bins: 16 compass points (22.5° each)
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
                      'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW']

  // Speed ranges in mph
  const speedRanges = [
    { name: '< 1 mph', min: 0, max: 1 },
    { name: '1-3 mph', min: 1, max: 3 },
    { name: '4-7 mph', min: 4, max: 7 },
    { name: '8-12 mph', min: 8, max: 12 },
    { name: '13-18 mph', min: 13, max: 18 },
    { name: '19-24 mph', min: 19, max: 24 },
    { name: '> 24 mph', min: 24, max: Infinity }
  ]

  // Initialize counts matrix [direction][speedRange]
  const counts = Array(16).fill(0).map(() => Array(7).fill(0))

  // Process each data point
  let validPoints = 0
  let invalidPoints = 0
  const samplePoints = []

  windData.forEach((point, index) => {
    const { winddir, windspeedmph } = point

    // Skip invalid data
    if (winddir == null || windspeedmph == null) {
      invalidPoints++
      return
    }

    validPoints++

    // Collect first 5 samples for logging
    if (samplePoints.length < 5) {
      samplePoints.push({ winddir, windspeedmph })
    }

    // Calculate direction bin (0-15)
    // 0° = N, 90° = E, 180° = S, 270° = W
    const directionIndex = Math.floor(((winddir + 11.25) % 360) / 22.5)

    // Find speed range bin
    const speedIndex = speedRanges.findIndex(range =>
      windspeedmph >= range.min && windspeedmph < range.max
    )

    if (speedIndex !== -1) {
      counts[directionIndex][speedIndex]++
    }
  })

  // Convert counts to percentages
  const total = validPoints

  // Build series data (one series per speed range)
  const series = speedRanges.map((range, speedIdx) => ({
    name: range.name,
    data: counts.map(directionCounts =>
      total > 0 ? (directionCounts[speedIdx] / total) * 100 : 0
    )
  }))

  return { directions, series }
}

export default function WindRoseWidget({ currentDevice, user }) {
  const [windData, setWindData] = useState([])
  const [scriptsLoaded, setScriptsLoaded] = useState(false)
  const [containerSize, setContainerSize] = useState(400) // Default size
  const containerRef = useRef(null)
  const chartRef = useRef(null)

  const {
    deviceData,
    getDeviceData,
    generateDeviceDataDataKey,
    isDeviceDataPending,
    isDeviceDataComplete
  } = useDeviceData()

  // Measure container size for square chart
  useEffect(() => {
    if (!containerRef.current) return

    const updateSize = () => {
      if (!containerRef.current) return
      const width = containerRef.current.offsetWidth
      if (width > 0) {
        setContainerSize(width)

        // If chart exists, trigger resize
        if (chartRef.current && chartRef.current.chart) {
          chartRef.current.chart.setSize(width, width, false)
        }
      }
    }

    // Give the DOM time to render before measuring
    const timeoutId = setTimeout(updateSize, 100)

    const resizeObserver = new ResizeObserver(updateSize)
    resizeObserver.observe(containerRef.current)

    return () => {
      clearTimeout(timeoutId)
      resizeObserver.disconnect()
    }
  }, [scriptsLoaded, windData])

  // Load Highcharts scripts
  useEffect(() => {
    // Check if already loaded
    if (window.Highcharts) {
      setScriptsLoaded(true)
      return
    }

    // Load scripts sequentially to ensure proper module loading
    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        // Check if script already exists in DOM
        const existingScript = document.querySelector(`script[src="${src}"]`)
        if (existingScript) {
          resolve()
          return
        }

        const script = document.createElement('script')
        script.src = src
        script.onload = () => resolve()
        script.onerror = (error) => reject(error)
        document.body.appendChild(script)
      })
    }

    // Load highcharts.js first, then highcharts-more.js
    loadScript('/vendor/highcharts/highcharts.js')
      .then(() => loadScript('/vendor/highcharts/highcharts-more.js'))
      .then(() => setScriptsLoaded(true))
      .catch(error => {
        console.error('WindRoseWidget: Failed to load Highcharts scripts:', error)
      })
  }, [])

  // Memoize dataKey to prevent recalculation on every render
  const macAddress = currentDevice?.macAddress
  const dataKey = useMemo(() => {
    if (!macAddress) return null

    const end = moment().valueOf()
    const start = moment().subtract(24, 'hours').valueOf()
    return generateDeviceDataDataKey(macAddress, start, end)
  }, [macAddress, generateDeviceDataDataKey])

  // Fetch data when component mounts or device changes
  useEffect(() => {
    if (dataKey) {
      deviceData(dataKey)
    }
  }, [dataKey, deviceData])

  // Update local state when data is available
  useEffect(() => {
    const isComplete = isDeviceDataComplete(dataKey)
    if (dataKey && isComplete) {
      const data = getDeviceData(dataKey)
      setWindData(data)
    }
  }, [dataKey, getDeviceData, isDeviceDataComplete])

  // Build chart options
  const chartOptions = useMemo(() => {
    if (windData.length === 0) return null

    const { directions, series } = processWindDataForRose(windData)

    // Determine text color based on theme
    // Light mode: #313236 (@gray-dark-amb)
    // Dark mode: #f2f3f8 (--color-white)
    const textColor = getTheme(user) === 'dark' ? '#f2f3f8' : '#313236'

    return {
      chart: {
        polar: true,
        type: 'column',
        backgroundColor: 'transparent',
        height: containerSize,
        width: containerSize,
        spacingTop: 0,
        spacingBottom: 80,
        spacingLeft: 0,
        spacingRight: 0
      },

      colors: [
        '#a6def6', // < 1 mph - blue-med (calm)
        '#93DED1', // 1-3 mph - bar-green-light (light air)
        '#55AF9F', // 4-7 mph - bar-green (light breeze)
        '#09a8e6', // 8-12 mph - blue (gentle breeze)
        '#748aa9', // 13-18 mph - blue-dark (moderate breeze)
        '#ffa903', // 19-24 mph - orange (fresh breeze)
        '#EB5757'  // > 24 mph - red-light (strong breeze+)
      ],

      title: {
        text: null
      },

      pane: {
        size: '80%'
      },

      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        itemStyle: {
          color: textColor,
          fontSize: '11px'
        }
      },

      xAxis: {
        categories: directions,
        tickmarkPlacement: 'on',
        lineColor: '#cdd4da',
        labels: {
          style: {
            fontSize: '11px',
            color: textColor
          }
        }
      },

      yAxis: {
        min: 0,
        endOnTick: false,
        showLastLabel: true,
        title: {
          text: 'Frequency (%)',
          style: {
            color: textColor
          }
        },
        labels: {
          format: '{value}%',
          style: {
            color: textColor
          }
        },
        reversedStacks: false
      },

      tooltip: {
        valueSuffix: '%',
        valueDecimals: 1,
        shared: true
      },

      plotOptions: {
        series: {
          stacking: 'normal',
          shadow: false,
          groupPadding: 0,
          pointPlacement: 'on'
        }
      },

      series: series,

      credits: {
        enabled: false
      }
    }
  }, [windData, containerSize, user])

  // Handle missing device
  if (!currentDevice || !currentDevice.macAddress) {
    return (
      <div className="device-wind-rose-widget">
        No device selected
      </div>
    )
  }

  // Handle loading state
  if (!scriptsLoaded || isDeviceDataPending(dataKey)) {
    return (
      <div className="device-wind-rose-widget loading">
        <Loader />
      </div>
    )
  }

  // Handle no data
  if (windData.length === 0) {
    return (
      <div className="device-wind-rose-widget no-data">
        No wind data available for the past 24 hours
      </div>
    )
  }

  return (
    <div className="device-wind-rose-widget" ref={containerRef}>
      {chartOptions && window.Highcharts && (
        <HighchartsReact
          ref={chartRef}
          highcharts={window.Highcharts}
          options={chartOptions}
        />
      )}

      {chartOptions && !window.Highcharts && (
        <div>Waiting for Highcharts to load...</div>
      )}
    </div>
  )
}

WindRoseWidget.propTypes = {
  currentDevice: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired
}

WindRoseWidget.defaultProps = {}

WindRoseWidget.displayName = 'WindRoseWidget'
